// Single owner of the decode-option sets. Ported from event-connect's scanner
// (decoder/decode-options.ts) — without this, a Worker path and a no-Worker fallback path can
// quietly drift apart (e.g. tryRotate/tryInvert enabled on one, disabled on the other). This
// file is the pure downgrade decision that reads the device's own telemetry instead of assuming
// every phone has the headroom the Worker-offload path was built and measured on.

import { FRAME_BUDGET_MS, type ScanTelemetrySummary } from '../telemetry'
import type { DecodeOptions } from './worker-protocol'

/** tryDownscale is explicit false on both sets — see worker-protocol.ts's DecodeOptions doc. */
export const THOROUGH: DecodeOptions = { tryHarder: true, tryRotate: true, tryInvert: true, tryDownscale: false }
export const LEAN: DecodeOptions = { tryHarder: true, tryRotate: false, tryInvert: false, tryDownscale: false }

/** Samples needed before the downgrade decision trusts the p95 it's looking at. */
const MIN_SAMPLES_BEFORE_DOWNGRADE = 20

/**
 * One-way downgrade within a camera configuration: THOROUGH → LEAN when p95 exceeds the frame
 * budget, never back — nothing to oscillate. `justReconfigured` re-arms the decision (starts
 * fresh from THOROUGH) after the camera pipeline actually changed, so a device that downgraded
 * early doesn't stay LEAN once the pipeline improves.
 */
export function chooseDecodeOptions(
  current: DecodeOptions,
  summary: ScanTelemetrySummary,
  justReconfigured: boolean,
): DecodeOptions {
  if (justReconfigured) return THOROUGH
  if (current === LEAN) return LEAN
  if (summary.decodes < MIN_SAMPLES_BEFORE_DOWNGRADE) return current
  return summary.p95 > FRAME_BUDGET_MS ? LEAN : current
}
